/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2023. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { defineMessages } from 'react-intl';

export default defineMessages({
  title: {
    id: 'Signup_Title',
    defaultMessage: 'Sign up',
  },
  subtitle: {
    id: 'Signup_Subtitle',
    defaultMessage: "To set up a new account we'll need you to enter the following details",
  },
  firstName: {
    id: 'Signup_LabelFirstName',
    defaultMessage: 'First Name',
  },
  surname: {
    id: 'Signup_LabelSurname',
    defaultMessage: 'Last Name',
  },
  DOB: {
    id: 'Signup_LabelDOB',
    defaultMessage: 'Date Of Birth',
  },
  genderType: {
    id: 'Signup_LabelGender',
    defaultMessage: 'Gender',
  },
  emailAddress: {
    id: 'Signup_LabelEmailAddress',
    defaultMessage: 'Email',
  },
  emailAddressHint: {
    id: 'Signup_LabelEmailAddressHint',
    defaultMessage: 'You will use this email to receive updates',
  },
  DOBHint: {
    id: 'Signup_LabelDOBHint',
    defaultMessage: 'Please enter in yyyy-mm-dd format',
  },
  username: {
    id: 'Signup_LabelUsername',
    defaultMessage: 'Username',
  },
  noEmail: {
    id: 'Signup_LabelNoEmail',
    defaultMessage: "I don't have an email address",
  },
  password: {
    id: 'Signup_LabelPassword',
    defaultMessage: 'Password',
  },
  secretQuestionType: {
    id: 'Signup_LabelSecretQuestionType',
    defaultMessage: 'Security question',
  },
  secretQuestionTypePlaceholder: {
    id: 'Signup_LabelSecretQuestionType_Placeholder',
    defaultMessage: 'Select a security question...',
  },
  genderTypePlaceholder: {
    id: 'Signup_LabelGenderType_Placeholder',
    defaultMessage: 'Select Gender...',
  },
  secretQuestionAnswer: {
    id: 'Signup_LabelSecretQuestionAnswer',
    defaultMessage: 'Security question answer',
  },
  termsAndConditions: {
    id: 'Signup_LabelTermsAndConditions',
    defaultMessage: 'I agree to the {link}',
  },
  termsAndConditionsLink: {
    id: 'Signup_LabelTermsAndConditionsLink',
    defaultMessage: 'terms and conditions',
  },
  submitButton: {
    id: 'Signup_LabelSubmitButton',
    defaultMessage: 'Sign Up',
  },
  alreadyHaveAnAccount: {
    id: 'Signup_LabelAlreadyHaveAnAccount',
    defaultMessage: 'Already have an account? {link}',
  },
  firstNameMissing: {
    id: 'Signup_LabelFirstName_Missing',
    defaultMessage: 'First Name must be entered.',
  },
  DOBMissing: {
    id: 'Signup_LabelDOB_Missing',
    defaultMessage: 'Date of Birth must be entered.',
  },
  DOBInFuture: {
    id: 'Signup_LabelDOB_In_Future',
    defaultMessage: 'Date of Birth cannot be in Future.',
  },
  DOBAgeLimit: {
    id: 'Signup_LabelDOB_Age_Limit',
    defaultMessage: 'The date of birth cannot be more than 150 years.',
  },
  genderTypeMissing: {
    id: 'Signup_LabelGender_Missing',
    defaultMessage: 'Gender must be entered.',
  },
  genderTypeMale: {
    id: 'Signup_GenderType_Male',
    defaultMessage: 'Male',
  },
  genderTypeFemale: {
    id: 'Signup_GenderType_Female',
    defaultMessage: 'Female',
  },
  genderTypeRefused: {
    id: 'Signup_GenderType_Refused',
    defaultMessage: 'Client Refused',
  },
  firstNameTooLong: {
    id: 'Signup_LabelFirstName_TooLong',
    defaultMessage: 'First Name must be 25 or fewer characters.',
  },
  surnameMissing: {
    id: 'Signup_LabelSurname_Missing',
    defaultMessage: 'Last Name must be entered.',
  },
  surnameTooLong: {
    id: 'Signup_LabelSurname_TooLong',
    defaultMessage: 'Surname must be 30 or fewer characters.',
  },
  emailAddressMissing: {
    id: 'Signup_LabelEmailAddress_Missing',
    defaultMessage: 'Email must be entered.',
  },
  emailAddressInvalidNoAtSymbol: {
    id: 'Signup_LabelEmailAddress_InvalidNoAtSymbol',
    defaultMessage:
      "'{email}' is not a valid email address. Valid email addresses must contain the @ character.",
  },
  emailAddressTooShort: {
    id: 'Signup_LabelEmailAddress_TooShort',
    defaultMessage: 'Email must be at least {usernameMinLength} characters.',
  },
  emailAddressTooLong: {
    id: 'Signup_LabelEmailAddress_TooLong',
    defaultMessage: 'Email can be at most {usernameMaxLength} characters.',
  },
  usernameMissing: {
    id: 'Signup_LabelUsername_Missing1',
    defaultMessage: 'Username must be entered.',
  },
  usernameInvalidTooShort: {
    id: 'Signup_LabelUsername_InvalidTooShort',
    defaultMessage: 'Username must be at least {usernameMinLength} characters.',
  },
  usernameInvalidTooLong: {
    id: 'Signup_LabelUsername_InvalidTooLong',
    defaultMessage: 'Username can be at most {usernameMaxLength} characters.',
  },
  passwordMissing: {
    id: 'Signup_LabelPassword_Missing',
    defaultMessage: 'Password must be entered.',
  },
  passwordInvalidTooShort: {
    id: 'Signup_LabelPassword_InvalidTooShort',
    defaultMessage: 'Password must be at least {passwordMinLength} characters.',
  },
  passwordInvalidTooLong: {
    id: 'Signup_LabelPassword_InvalidTooLong',
    defaultMessage: 'Password can be at most {passwordMaxLength} characters.',
  },
  passwordInvalidNoNumbersOrSpecialCharacters: {
    id: 'Signup_LabelPassword_InvalidNoNumbersOrSpecialCharacters',
    defaultMessage: 'Password must contain at least 1 number(s) and/or special characters.',
  },
  passwordInvalidNumberUpperLowerSpecialCharacters: {
    id: 'Signup_LabelPassword_InvalidNumberUpperLowerSpecialCharacters',
    defaultMessage:
      'Password entered does not meet minimum requirements indicated above, please try again.',
  },
  passwordCannotMatchUserName: {
    id: 'Signup_LabelPassword_CannotMatchUserName',
    defaultMessage: 'Password cannot match your username.',
  },
  usernameInvalidNoSpecialCharacters: {
    id: 'Signup_LabelUsername_InvalidNoSpecialCharacters',
    defaultMessage: 'Username must be Alphanumeric only.',
  },
  secretQuestionTypeMissing: {
    id: 'Signup_LabelSecretQuestionType_Missing',
    defaultMessage: 'You must select a security question.',
  },
  secretQuestionAnswerMissing: {
    id: 'Signup_LabelSecretQuestionAnswer_Missing',
    defaultMessage: 'You must provide an answer for the selected security question.',
  },
  termsAndConditionsAcceptedMissing: {
    id: 'Signup_LabelTermsAndConditionsAccepted_Missing',
    defaultMessage: 'You must accept the terms and conditions to proceed.',
  },
  secretQuestion1: {
    id: 'Signup_LabelSecretQuestion_1',
    defaultMessage: "Mother's maiden name",
  },
  ariaLabelPanelDescription: {
    id: 'Signup_AriaLabelPanelDescription',
    defaultMessage: 'Panel Description',
  },
  loginLinkLabel: {
    id: 'Signup_LoginLinkLabel',
    defaultMessage: 'Login',
  },
  showPasswordText: {
    id: 'Signup_ShowPasswordText',
    defaultMessage: 'Show password',
  },
  hidePasswordText: {
    id: 'Signup_HidePasswordText',
    defaultMessage: 'Hide password',
  },
  usernameAlreadyInUseErrorText: {
    id: 'Signup_UsernameAlreadyInUseErrorText',
    defaultMessage:
      'The username entered has already been assigned to another User. Please enter a different name.',
  },
  homeButton: {
    id: 'Login_HomeButton',
    defaultMessage: 'Home',
  },
  mandatoryFieldsAlertText: {
    id: 'Signup_MandatoryFieldsAlertText',
    defaultMessage: 'Complete all fields',
  },
  duplicateAccountAlertText1: {
    id: 'Signup_DuplicateAccountAlertText1',
    defaultMessage:
      'Do not create a new account if one was created in the past. Please return to the log-in page to access your existing account.',
  },
  duplicateAccountAlertText2: {
    id: 'Signup_DuplicateAccountAlertText2',
    defaultMessage:
      'Creating multiple accounts will delay the processing of your application and may prevent the submittal and/or processing of your application.',
  },
  signupSuccessToastText: {
    defaultMessage: 'You have successfully signed up',
    id: 'SignupSuccessToast_text',
  },
  signupSuccessActivationLinkText: {
    defaultMessage:
      'An activation link has been sent to your registered email address. Please check your email inbox and click on the activation link to complete the account registration process. You have 24 hours to click on the activation link.',
    id: 'SignupSuccessActivationLink_text',
  },
  signupDoBFormat: {
    defaultMessage: 'Please enter the format in dd/MM/yyyy',
    id: 'SignupDoBFormat',
  },
  passwordCharactersCriteria: {
    id: 'Signup_LabelPassword_PasswordCharactersCriteria',
    defaultMessage: 'Please use the following criteria when creating a password:',
  },
  passwordCharactersCriteria1: {
    id: 'Signup_LabelPassword_PasswordCharactersCriteria1',
    defaultMessage: 'Password must be at least {passwordMinLength} characters in length.',
  },
  passwordCharactersCriteria2: {
    id: 'Signup_LabelPassword_PasswordCharactersCriteria2',
    defaultMessage: 'Password must contain uppercase and lowercase characters.',
  },
  passwordCharactersCriteria3: {
    id: 'Signup_LabelPassword_PasswordCharactersCriteria3',
    defaultMessage: 'Password must contain at least one number.',
  },
  passwordCharactersCriteria4: {
    id: 'Signup_LabelPassword_PasswordCharactersCriteria4',
    defaultMessage: 'Password must contain at least one special character.',
  },
  passwordCharactersCriteria5: {
    id: 'Signup_LabelPassword_PasswordCharactersCriteria5',
    defaultMessage: 'Password cannot match username.',
  },
  requiredIndicatorText: {
    id: 'Required_Indicator_Text',
    defaultMessage: 'Required',
  },
  minorAgeValidation: {
    id: 'Minor_Age_Validation',
    defaultMessage: 'You must be at least 18 years old to register for an account.',
  },
});
